import type {
  AdminExecution,
  AdminExecutionDetail,
  AutomationExecutionDetails,
  PaginationMeta,
  PaginatedResponse,
} from '@/types/admin.types';

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : undefined;
}

function readString(...values: unknown[]): string | undefined {
  return values.find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0
  );
}

function readNullableString(...values: unknown[]): string | null {
  return readString(...values) ?? null;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function calculateDuration(startedAt: string | null, completedAt: string | null): number | null {
  if (!startedAt || !completedAt) return null;

  const start = Date.parse(startedAt);
  const end = Date.parse(completedAt);
  return Number.isFinite(start) && Number.isFinite(end) && end >= start ? end - start : null;
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function readSteps(value: unknown): { name: string; status: string; timestamp: string }[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    const step = asRecord(item);
    const name = readString(step?.name);
    const status = readString(step?.status);
    const timestamp = readString(step?.timestamp);

    return name && status && timestamp ? [{ name, status, timestamp }] : [];
  });
}

export function normalizeAdminExecution(value: unknown): AdminExecutionDetail {
  const execution = asRecord(value) ?? {};
  const configuration = asRecord(execution.configuration);
  const folder = asRecord(configuration?.folder);
  const domain = asRecord(configuration?.domain);
  const organisation = asRecord(domain?.organisation);
  const details = asRecord(execution.details) as AutomationExecutionDetails | undefined;

  const startedAt = readNullableString(
    execution.startedAt,
    execution.scheduledAt,
    execution.createdAt
  );
  const completedAt = readNullableString(execution.completedAt);

  return {
    uuid: readString(execution.uuid) ?? 'execution-inconnue',
    templateName:
      readString(
        execution.templateName,
        execution.templateId,
        configuration?.templateName,
        configuration?.templateId
      ) ?? 'Automatisation',
    domainName: readString(execution.domainName, domain?.name) ?? 'Domaine non renseigné',
    folderName: readString(execution.folderName, folder?.name) ?? 'Dossier non renseigné',
    status: readString(execution.status) ?? 'unknown',
    startedAt,
    completedAt,
    duration: readNumber(execution.duration) ?? calculateDuration(startedAt, completedAt),
    organisationName:
      readString(execution.organisationName, organisation?.name) ?? 'Organisation non renseignée',
    error: readNullableString(execution.error),
    details: details ?? null,
    effectOutcome: readString(execution.effectOutcome) as
      AdminExecution['effectOutcome'] | undefined,
    effectAlert: typeof execution.effectAlert === 'boolean' ? execution.effectAlert : undefined,
    effectIssues: readStringArray(execution.effectIssues),
    retryBlocked: typeof execution.retryBlocked === 'boolean' ? execution.retryBlocked : undefined,
    logs: readStringArray(execution.logs),
    screenshots: readStringArray(execution.screenshots ?? details?.screenshots),
    steps: readSteps(execution.steps ?? details?.steps),
  };
}

function readPagination(value: unknown, itemCount: number): PaginationMeta {
  const pagination = asRecord(value);
  const currentPage = readNumber(pagination?.currentPage) ?? 1;
  const itemsPerPage = readNumber(pagination?.itemsPerPage) ?? itemCount;
  const totalItems = readNumber(pagination?.totalItems) ?? itemCount;
  const totalPages =
    readNumber(pagination?.totalPages) ??
    (itemsPerPage > 0 ? Math.ceil(totalItems / itemsPerPage) : 1);

  return {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    hasNext:
      typeof pagination?.hasNext === 'boolean' ? pagination.hasNext : currentPage < totalPages,
    hasPrevious:
      typeof pagination?.hasPrevious === 'boolean' ? pagination.hasPrevious : currentPage > 1,
  };
}

export function normalizeAdminExecutionsPage(value: unknown): PaginatedResponse<AdminExecution> {
  const payload = asRecord(value);
  const rawItems = Array.isArray(payload?.data) ? payload.data : [];
  const data = rawItems.map(normalizeAdminExecution);

  return {
    data,
    pagination: readPagination(payload?.pagination, data.length),
  };
}
